import { max } from 'lodash'

import { FileManagerFile } from './fileManagerSlice'
import { useGetDocumentTemplatesQuery } from '@/services/documents'

export function useFileManager() {
  const { data: documentTemplates, isSuccess: documentTemplatesLoaded }: any =
    useGetDocumentTemplatesQuery()

  const documentNamesOptions =
    documentTemplates
      ?.map((doc) => ({
        label: doc.name,
        value: doc.uuid,
        category: doc.category,
        tag: doc.tag,
        kind: doc.kind,
        aliases: doc.aliases?.split(',').map((alias) => alias.toLowerCase().trim()),
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) ?? []

  /** Allow any character except whitespace at the beginning and end of the string */
  const fileRenameRegex = /^\S.*\S$|^\S$/

  interface GroupedOption {
    label: string
    options: object
  }

  const calculateSuggestedGroupedOptions = (
    filesToUpload: File[] | FileManagerFile[]
  ): Array<GroupedOption[]> => {
    const filenameMatches = findFileNameMatches(filesToUpload)

    // All matches that have a value over 0 should be considered as suggested
    const suggestedOptionIndexes = filenameMatches.map((fileMatch) => {
      const suggestedOptions = fileMatch.reduce((accumulator, matchAmount, index) => {
        if (matchAmount > 0) {
          accumulator.push([index, matchAmount])
        }
        return accumulator
      }, [])

      // Sort by highest matching value
      suggestedOptions.sort((a, b) => b[1] - a[1])
      return suggestedOptions
    })

    const suggestedNameOptions = suggestedOptionIndexes.map((fileIndex) =>
      fileIndex.map((index) => documentNamesOptions[index[0]])
    )

    const groupedOptions: Array<GroupedOption[]> = filesToUpload.map((file, index) => {
      return [
        {
          label: 'Suggested',
          options: suggestedNameOptions[index],
        },
        {
          label: 'All options',
          options: documentNamesOptions.filter(
            // Remove suggested options from all options
            (itemA) => !suggestedNameOptions[index].some((itemB) => itemA.label === itemB.label)
          ),
        },
      ]
    })

    return groupedOptions
  }

  /** Finds filename matches with doc label or aliases */
  const findFileNameMatches = (filesToUpload: File[] | FileManagerFile[]) => {
    const filenameMatches = filesToUpload.map((file) => {
      const fileName = file?.name || file?.fileName // Support File & FileManagerFile
      const filenameWithoutExtension = fileName
        .toLowerCase()
        .split('.')
        .slice(0, -1)
        .join()
        .replaceAll(' - ', ' ')

      const filenameWords = filenameWithoutExtension.split(/[\s_]+/)

      const documentLabelsSets = documentNamesOptions.map((doc) => {
        const labelWords = doc.label.toLowerCase().split(' ') ?? []
        const labelWordsSet = new Set(labelWords)

        const labelIntersection = new Set(
          [...filenameWords].filter((word) => labelWordsSet.has(word))
        )
        return labelIntersection.size
      })

      const documentAliasSets = documentNamesOptions.map((doc) => {
        const aliasMatches = doc.aliases?.map((alias) => {
          const aliasWords = alias.split(' ')
          const aliasWordsSet = new Set(aliasWords)

          const aliasIntersection = new Set(
            [...filenameWords].filter((word) => aliasWordsSet.has(word))
          )
          return aliasIntersection.size
        })
        // Return only the highest amount of match for all aliases
        return max(aliasMatches)
      })
      return documentLabelsSets.some((item) => item > 0) ? documentLabelsSets : documentAliasSets
    })
    return filenameMatches
  }

  return {
    documentNamesOptions,
    documentTemplatesLoaded,
    fileRenameRegex,
    calculateSuggestedGroupedOptions,
  }
}
